// The web-visible half of a list query. A route schema names its list's columns here, by their STORED paths, and embeds
// the pieces this returns as its `where` / `sorted` (and `column`) members. Nothing here knows the collection: the server
// half (packages/server/helpers/queryContract.ts) checks the names against the stored model, kinds them and binds them to
// the route. So route schemas, and the web/mobile programs that import them, import no collection module and no server
// code, and a *.collection.ts may keep importing a *.schema.ts.
//
// The pieces are filterWhereClauseFor / sortConfigFor's Zod code (common.schema.ts delegates to it), so a list keeps the
// same z.input, z.output and published JSON Schema whichever of the two builds its pieces.
import { z } from "zod";
import {
  customFieldFilterPattern,
  filterOperatorDescription,
  filterOperators,
  formQuestionFilterPattern,
  type FilterWhereCondition,
} from "./filterPrimitives.js";
import { countNumber, nonMoneyNumber } from "./numberKinds.js";

/** A dynamic column family: `customFields` / `projectLevelCustomFields`, another record member, or `formAnswers`. */
export type Family = string;

export interface ColumnsSpec {
  /** Columns a client may filter on: STORED paths (or a member the server half joins/computes), never an alias. */
  readonly filter: readonly string[];
  /** Columns a client may sort on; the filter columns when omitted. */
  readonly sort?: readonly string[];
  /** Dynamic families; the server half accepts only families the collection model stores. */
  readonly dynamic?: readonly Family[];
  /** The route also takes a column NAME as data (distinct values, group-by): a `column` member, one of the filter columns. */
  readonly column?: true;
}

/** The columns of one list route. `key` is `<mount><path>` of the route that serves it. */
export interface QueryColumns<
  in out K extends string,
  in out Spec extends ColumnsSpec,
> {
  readonly key: K;
  readonly spec: Spec;
  /** The route's `where` member (`.meta()` it freely: a clone keeps its identity through Zod's parent chain). */
  readonly where: WhereSchema;
  /** The route's `sorted` member. */
  readonly sorted: SortedSchema;
  /** The route's `column` member (only when the spec says `column: true`). */
  readonly column: ColumnSchema;
}

/** What every columns object has, with no type parameters. */
export interface QueryColumnsBase {
  readonly key: string;
  readonly spec: ColumnsSpec;
  readonly where: WhereSchema;
  readonly sorted: SortedSchema;
  readonly column: ColumnSchema;
}

/** The names a columns object admits, with the defaults applied (sort = filter, no families). */
export const namesOf = (spec: ColumnsSpec): ColumnNames => ({
  filter: spec.filter,
  sort: spec.sort ?? spec.filter,
  dynamic: spec.dynamic ?? [],
});

/** Whether a column belongs to a family, by the custom-field / form-question patterns or `<member>.<key>`. */
export function familyAdmits(family: Family, column: string): boolean {
  if (family === "formAnswers") return formQuestionFilterPattern.test(column);
  if (family === "customFields" || family === "projectLevelCustomFields") {
    return (
      column.startsWith(`${family}.`) && customFieldFilterPattern.test(column)
    );
  }
  return (
    column.startsWith(`${family}.`) &&
    column.length > family.length + 1 &&
    !column.includes("$")
  );
}

/**
 * Names one global search may list: as many as any contract can search (queryContract.ts: the standard 64, raised at
 * most ten times by a limitException). The piece reads no name past it, so a longer list is refused at the cost of 641
 * names, whatever its length. SlickGrid1 names each column it shows once: at most 568 (Databank views).
 */
export const MAX_SEARCH_NAMES = 640;

/** Characters of a client's text a refusal repeats: the house limit for identifiers. */
const QUOTED_TEXT = 128;
/** A client's text as a refusal repeats it, cut to QUOTED_TEXT characters. */
export const clipped = (text: string): string =>
  text.length > QUOTED_TEXT ? `${text.slice(0, QUOTED_TEXT)}…` : text;
/** A client's text as a refusal quotes it: clipped, in double quotes. */
export const quoted = (text: string): string => `"${clipped(text)}"`;

export interface ColumnNames {
  readonly filter: readonly string[];
  readonly sort: readonly string[];
  readonly dynamic: readonly Family[];
  /**
   * filterWhereClauseFor / sortConfigFor's allow-patterns: a column one matches is admitted too. A columns object has
   * none.
   */
  readonly patterns?: readonly RegExp[];
}
const admitsDynamic = (c: ColumnNames, column: string) =>
  c.dynamic.some((f) => familyAdmits(f, column)) ||
  (c.patterns ?? []).some((re) => re.test(column));
const admitsFilter = (c: ColumnNames, field: string) =>
  c.filter.includes(field) || admitsDynamic(c, field);
const admitsSort = (c: ColumnNames, name: string) =>
  c.sort.includes(name) || admitsDynamic(c, name);
const listText = (names: readonly string[], c: ColumnNames) =>
  names.join(", ") +
  (c.dynamic.length > 0 || (c.patterns ?? []).length > 0
    ? " (plus dynamic custom-field columns)"
    : "");

/**
 * A list's `where` piece: an ARRAY of conditions (flat, grouped, or a global search, which is narrowed to the list
 * rather than refused unless it lists more than MAX_SEARCH_NAMES), each `field` one of the list's columns. `refine` adds
 * the server half's checks (kinds, values, pins) to ITS private copy; the published piece passes none.
 *
 * ⚠️ KNOWN BUG (resource safety, deferred by the user 2026-10-14): the published where and sorted pieces parse every
 * element before the contract's caps run (the route schema comes first in the chain), so their cost is linear in the
 * body, bounded only by its 1024 MB limit: 10,000 × 100 values (10.75 MB) take 349 ms before a no-access caller is
 * refused, a 1,000,000-entry sorted (42.9 MB) 879 ms. Fix: make queryContract.ts's excessOf walk (and a length check on
 * sorted) this piece's first step, at the ceilings (a piece does not know its contract's own caps), so an oversize where
 * is one issue and nothing is parsed; validate-request.ts's KNOWN BUG note is the same class.
 */
export function makeWhereSchema(
  c: ColumnNames,
  refine?: {
    readonly leaf?: (
      cond: FilterWhereCondition,
      ctx: z.core.$RefinementCtx,
    ) => void;
    readonly top?: (
      nodes: readonly FilterWhereCondition[],
      ctx: z.core.$RefinementCtx,
    ) => void;
    /**
     * Parse the declared members only and drop every other key (the server's private copy, which it then copies). The
     * published piece keeps a condition's other keys, as today's factories always have.
     */
    readonly declaredOnly?: boolean;
  },
) {
  const allowedList = listText(c.filter, c);
  // Scalars and arrays of scalars only: never an object, so a `{ $ne: … }` cannot ride in as a value.
  const filterScalar = z.union([
    z.string(),
    nonMoneyNumber,
    z.boolean(),
    z.null(),
  ]);
  const filterValue = z.union([filterScalar, z.array(filterScalar)]).optional();
  // A preprocess, not a `.transform()`: z.toJSONSchema cannot represent a transform, and this piece is published.
  // `declared`: the keys the private copy parses. Its copy is made of those only, so a key it drops is never copied.
  const narrowGlobalSearch = (
    raw: unknown,
    ctx: z.core.$RefinementCtx,
    declared: readonly string[] | undefined,
  ) => {
    if (
      typeof raw !== "object" ||
      raw === null ||
      !("operator" in raw) ||
      raw.operator !== "globalsearch" ||
      !("field" in raw) ||
      typeof raw.field !== "string"
    )
      return raw;
    const names: string[] = [];
    for (let start = 0; ;) {
      const comma = raw.field.indexOf(",", start);
      if (names.length === MAX_SEARCH_NAMES) {
        ctx.addIssue({
          code: "custom",
          path: ["field"],
          message: `Too many fields: more than ${MAX_SEARCH_NAMES} in one global search.`,
        });
        return raw;
      }
      names.push(raw.field.slice(start, comma === -1 ? undefined : comma));
      if (comma === -1) break;
      start = comma + 1;
    }
    const field = names
      .map((f) => f.trim())
      .filter((f) => admitsFilter(c, f))
      .join(",");
    if (declared === undefined) return { ...raw, field };
    const node: Record<string, unknown> = {};
    for (const key of declared)
      if (key in raw) node[key] = Reflect.get(raw, key);
    node["field"] = field;
    return node;
  };
  const checked = (schema: z.ZodType<FilterWhereCondition>) =>
    schema.superRefine((cond, ctx) => {
      // A group carries no field (its predicates are checked one by one), and a global search was narrowed above.
      if (
        cond.operator !== "globalsearch" &&
        typeof cond.field === "string" &&
        cond.field.length > 0 &&
        !admitsFilter(c, cond.field)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["field"],
          message: `Filtering on ${quoted(cond.field)} is not allowed. Filterable fields: ${allowedList}.`,
        });
      }
      refine?.leaf?.(cond, ctx);
    });
  // The grids' case and accent flags, which no builder reads: kept when they are booleans, dropped otherwise.
  const gridFlag = z.boolean().optional().catch(undefined);
  // A recursive schema needs this annotation; naming the node type makes z.infer report FilterWhereCondition. The input
  // side stays `unknown`, which is what a preprocess accepts.
  const condition: z.ZodType<FilterWhereCondition> = z.lazy(() => {
    const members = {
      field: z.string().optional(),
      // Closed to the operators the builders implement, so a guessed one ("eq") is refused instead of dropped.
      // Optional: a leaf without one means "equal", and a group carries none.
      operator: z
        .enum(filterOperators)
        .optional()
        .meta({ description: filterOperatorDescription }),
      value: filterValue,
      isComplex: z.boolean().optional(),
      // Closed to and/or: it becomes the group's MongoDB operator, so a free string could pick `$nor` or `$where`.
      condition: z.enum(["and", "or"]).optional().meta({
        description: 'How a group joins its predicates: "and" or "or".',
      }),
      predicates: z.array(condition).optional(),
    };
    if (!refine?.declaredOnly)
      return z.preprocess(
        (raw, ctx) => narrowGlobalSearch(raw, ctx, undefined),
        checked(z.looseObject(members)),
      );
    const declared = {
      ...members,
      ignoreCase: gridFlag,
      ignoreAccent: gridFlag,
    };
    const keys = Object.keys(declared);
    return z.preprocess(
      (raw, ctx) => narrowGlobalSearch(raw, ctx, keys),
      checked(z.object(declared)),
    );
  });
  const list = refine?.top
    ? z.array(condition).superRefine((nodes, ctx) => refine.top?.(nodes, ctx))
    : z.array(condition);
  return list.optional().meta({
    description:
      "Filter for list/grid queries — an ARRAY of conditions ANDed together, e.g. " +
      `[{ field: "${c.filter[0] ?? "name"}", operator: "contains", value: "Tower-A" }]. ` +
      `Only these fields may be filtered: ${allowedList}.`,
  });
}
export type WhereSchema = ReturnType<typeof makeWhereSchema>;

/**
 * A list's `sorted` piece. Only the first entry is applied, and an omitted direction sorts descending, so only the first
 * is held to the sortable columns. SlickGrid1 sends every sort condition of a saved view, and a later one may name any
 * column of the grid; it is never applied, so it must only be well-formed.
 */
export function makeSortedSchema(c: ColumnNames) {
  const allowedList = listText(c.sort, c);
  const entry = z.object({
    // Non-empty: an empty name would become the sort key "", which MongoDB refuses.
    name: z
      .string()
      .min(1)
      .meta({
        description: `Column to sort by. The first entry, the one applied, names one of: ${allowedList}.`,
      }),
    direction: z.enum(["ascending", "descending"]).optional().meta({
      description: "Sort direction, defaults to descending if not specified",
    }),
  });
  return z
    .array(entry)
    .superRefine(([applied], ctx) => {
      if (applied !== undefined && !admitsSort(c, applied.name)) {
        ctx.addIssue({
          code: "custom",
          path: [0, "name"],
          message: `Sorting on ${quoted(applied.name)} is not allowed. Sortable columns: ${allowedList}.`,
        });
      }
    })
    .optional()
    .meta({
      description:
        "Sort configuration for list/grid queries — an array of { name, direction? }; only the first entry is " +
        `applied server-side, and only its name must be one of these columns: ${allowedList}. Later entries are ` +
        "not applied and may name any column.",
    });
}
export type SortedSchema = ReturnType<typeof makeSortedSchema>;

/** A column NAME sent as data: one of the filter columns (or a family member). */
export function makeColumnSchema(c: ColumnNames) {
  const allowedList = listText(c.filter, c);
  return z
    .string()
    .min(1)
    .superRefine((name, ctx) => {
      if (!admitsFilter(c, name)) {
        ctx.addIssue({
          code: "custom",
          path: [],
          message: `${quoted(name)} is not a column of this list. Columns: ${allowedList}.`,
        });
      }
    })
    .meta({ description: `A column of this list: ${allowedList}.` });
}
export type ColumnSchema = ReturnType<typeof makeColumnSchema>;

// ─── identity: which columns object a schema piece belongs to (followed through .meta()/.describe() clones) ─────────
export type PieceRole = "where" | "sorted" | "column";
const ownerOfPiece = new WeakMap<
  object,
  { readonly columns: QueryColumnsBase; readonly role: PieceRole }
>();
const columnsByKey = new Map<string, QueryColumnsBase>();
let columnsSealed = false;

/** The columns object (and role) a route-schema member belongs to, or undefined for any other schema. */
export function pieceOwner(
  schema: unknown,
):
  { readonly columns: QueryColumnsBase; readonly role: PieceRole } | undefined {
  for (
    let s: unknown = schema;
    typeof s === "object" && s !== null && "_zod" in s;
  ) {
    const owner = ownerOfPiece.get(s);
    if (owner) return owner;
    const zod: unknown = Reflect.get(s, "_zod");
    s =
      typeof zod === "object" && zod !== null
        ? Reflect.get(zod, "parent")
        : undefined;
  }
  return undefined;
}
export const allColumns = (): readonly QueryColumnsBase[] => [
  ...columnsByKey.values(),
];
export function sealColumns(): void {
  columnsSealed = true;
}

/** Declare at module scope of a *.schema.ts, one per list route; a call after boot throws. */
export function queryColumns<
  const K extends string,
  const Spec extends ColumnsSpec,
>(
  key: K & (string extends K ? "a columns key is a string literal" : unknown),
  spec: Spec,
): QueryColumns<K, Spec> {
  if (columnsSealed)
    throw new Error(
      `queryColumns("${key}") after boot: declare columns at module scope`,
    );
  if (columnsByKey.has(key))
    throw new Error(`queryColumns("${key}") is declared twice`);
  const names = namesOf(spec);
  const columns = Object.freeze({
    key,
    spec,
    where: makeWhereSchema(names),
    sorted: makeSortedSchema(names),
    column: makeColumnSchema(names),
  });
  ownerOfPiece.set(columns.where, { columns, role: "where" });
  ownerOfPiece.set(columns.sorted, { columns, role: "sorted" });
  if (spec.column)
    ownerOfPiece.set(columns.column, { columns, role: "column" });
  columnsByKey.set(key, columns);
  return columns;
}

/** The SlickGrid request envelope carrying a list's own pieces, in the grids' member order. */
export function gridValue<C extends QueryColumnsBase>(columns: C) {
  return z.object({
    skip: countNumber.nonnegative().optional().meta({
      description:
        "Number of rows to skip for pagination (offset). Defaults to 0 when omitted.",
    }),
    take: countNumber.nonnegative().optional().meta({
      description:
        "Maximum number of rows to return in this page (page size). Defaults to 10 when omitted.",
    }),
    sorted: columns.sorted,
    where: columns.where,
    requiresCounts: z.boolean().optional().meta({
      description:
        "When true, the endpoint also returns the total matching row count (for grid pagination), not just the current page.",
    }),
  });
}
